// Demandes d'inscription (administrateurs) : accepter (compte client + invitation), refuser, effacer.
import { useCallback, useEffect, useState } from "react";
import { api, unwrap } from "../api/client";
import type { components } from "../api/schema";
import type { ClientLimits } from "../api/types";
import {
  Button, Checkbox, ConfirmDialog, CopyField, EmptyState, Field, FormError, Modal, PageHeader, Pill, Tag, TextInput, useToast,
} from "../components/ui";
import { formatTime } from "../lib/format";
import { N_, t } from "../lib/i18n";
import { DEFAULT_LIMITS, LimitsFields } from "./Users";

type Signup = components["schemas"]["SignupView"];
type Decision = components["schemas"]["SignupDecision"];

const STATUS: Record<Signup["status"], { label: string; tone: "ok" | "warn" | "muted" | "err" }> = {
  unconfirmed: { label: N_("Adresse à confirmer"), tone: "muted" },
  pending: { label: N_("À examiner"), tone: "warn" },
  approved: { label: N_("Acceptée"), tone: "ok" },
  rejected: { label: N_("Refusée"), tone: "err" },
};

/** Identifiant proposé : la partie avant le @, réduite aux caractères permis. */
function suggestUsername(email: string): string {
  return email.split("@")[0]!.replace(/[^A-Za-z0-9._-]/g, "").slice(0, 32) || "client";
}

function ApproveForm({ signup, onClose, onDone }: { signup: Signup; onClose: () => void; onDone: (d: Decision) => void }) {
  const [username, setUsername] = useState(suggestUsername(signup.email));
  const [limits, setLimits] = useState<ClientLimits>(DEFAULT_LIMITS);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      onDone(await unwrap(api.POST("/api/signups/{signup_id}/approve", {
        params: { path: { signup_id: signup.id } }, body: { username: username.trim(), limits },
      })));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open title={t("Accepter {name}", { name: `${signup.first_name} ${signup.last_name}` })} onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>{t("Annuler")}</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>{t("Créer le compte et envoyer l'invitation")}</Button>
      </>}>
      <div className="flex flex-col gap-4">
        <p className="text-[13px] text-muted">
          {t("Un compte client est créé. {email} reçoit un lien (valable 48 h, une seule fois) pour choisir son mot de passe : personne d'autre ne le connaîtra.", { email: signup.email })}
        </p>
        <Field label={t("Identifiant du client")} htmlFor="ap-user" hint={t("2 à 32 caractères : lettres, chiffres, . _ -")}>
          <TextInput id="ap-user" required autoFocus value={username} onChange={(e) => setUsername(e.target.value)} />
        </Field>
        <LimitsFields value={limits} onChange={setLimits} />
        <FormError message={error} />
      </div>
    </Modal>
  );
}

export function Requests() {
  const toast = useToast();
  const [items, setItems] = useState<Signup[] | null>(null);
  const [approving, setApproving] = useState<Signup | null>(null);
  const [rejecting, setRejecting] = useState<Signup | null>(null);
  const [notify, setNotify] = useState(false);
  const [deleting, setDeleting] = useState<Signup | null>(null);
  const [link, setLink] = useState<{ who: string; url: string; error: string | null } | null>(null);

  const load = useCallback(() => {
    unwrap(api.GET("/api/signups")).then(setItems).catch((e: Error) => toast(e.message, "err"));
  }, [toast]);
  useEffect(load, [load]);

  const decided = (d: Decision) => {
    if (d.email_sent) toast(t("Invitation envoyée à {email}", { email: d.signup.email }));
    else if (d.invite_link) setLink({ who: d.signup.email, url: d.invite_link, error: d.email_error ?? null });
    else toast(d.email_error ?? t("Invitation non envoyée"), "warn");
    load();
  };
  const resend = async (s: Signup) => {
    try {
      decided(await unwrap(api.POST("/api/signups/{signup_id}/resend", { params: { path: { signup_id: s.id } } })));
    } catch (e) {
      toast((e as Error).message, "err");
    }
  };
  const reject = async () => {
    if (!rejecting) return;
    try {
      const d = await unwrap(api.POST("/api/signups/{signup_id}/reject", { params: { path: { signup_id: rejecting.id } }, body: { notify } }));
      toast(notify && !d.email_sent ? t("Demande refusée, e-mail non envoyé : {error}", { error: d.email_error ?? "" }) : t("Demande refusée"), notify && !d.email_sent ? "warn" : "ok");
      load();
    } catch (e) {
      toast((e as Error).message, "err");
    }
    setRejecting(null);
  };
  const remove = async () => {
    if (!deleting) return;
    try {
      await unwrap(api.DELETE("/api/signups/{signup_id}", { params: { path: { signup_id: deleting.id } } }));
      toast(t("Demande effacée"));
      load();
    } catch (e) {
      toast((e as Error).message, "err");
    }
    setDeleting(null);
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={t("Demandes d'accès")}
        subtitle={t("Inscriptions reçues par le formulaire public. Ouvrir ou fermer les inscriptions : Réglages → Inscriptions.")} />
      {items === null ? <p className="text-muted">{t("Chargement…")}</p> : items.length === 0 ? (
        <EmptyState title={t("Aucune demande")}>{t("Les demandes apparaissent ici dès que la personne a confirmé son adresse.")}</EmptyState>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((s) => (
            <div key={s.id} className="rounded-lg border border-line bg-surface p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{s.first_name} {s.last_name}</span>
                    <Pill tone={STATUS[s.status].tone}>{t(STATUS[s.status].label)}</Pill>
                    <span title={t("Langue de la demande")}><Tag>{s.lang}</Tag></span>
                    {s.blocked && <Pill tone="err">{t("Liste noire")}</Pill>}
                  </div>
                  <div className="text-[13px] text-muted">
                    {s.email}{s.company ? ` · ${s.company}` : ""}{s.phone ? ` · ${s.phone}` : ""}
                  </div>
                  <div className="text-[12px] text-faint">
                    {t("reçue {time}", { time: formatTime(s.created) })}
                    {s.username && ` · ${t("compte « {name} »", { name: s.username })}`}
                    {s.status === "approved" && ` · ${s.invitation_used ? t("mot de passe choisi") : t("invitation en attente")}`}
                    {s.decided_by && ` · ${t("par {user}", { user: s.decided_by })}`}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(s.status === "pending" || s.status === "rejected") && !s.blocked && (
                    <Button size="sm" variant="primary" onClick={() => setApproving(s)}>{t("Accepter")}</Button>
                  )}
                  {(s.status === "pending" || s.status === "unconfirmed") && (
                    <Button size="sm" onClick={() => { setNotify(false); setRejecting(s); }}>{t("Refuser")}</Button>
                  )}
                  {s.status === "approved" && !s.invitation_used && (
                    <Button size="sm" onClick={() => resend(s)}>{t("Renvoyer l'invitation")}</Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setDeleting(s)}>{t("Effacer")}</Button>
                </div>
              </div>
              {s.usage && <p className="mt-2 whitespace-pre-line rounded-md bg-raised/40 px-3 py-2 text-[13px]">{s.usage}</p>}
            </div>
          ))}
        </div>
      )}

      {approving && <ApproveForm signup={approving} onClose={() => setApproving(null)}
        onDone={(d) => { setApproving(null); decided(d); }} />}
      <ConfirmDialog open={rejecting !== null} title={t("Refuser la demande ?")} confirmLabel={t("Refuser")}
        onCancel={() => setRejecting(null)} onConfirm={reject}>
        <div className="flex flex-col gap-3">
          <span>{t("{name} n'aura pas d'accès. La demande est effacée automatiquement après 30 jours.", { name: `${rejecting?.first_name ?? ""} ${rejecting?.last_name ?? ""}` })}</span>
          <Checkbox id="rj-notify" checked={notify} onChange={setNotify} label={t("Prévenir la personne par e-mail")} />
        </div>
      </ConfirmDialog>
      <ConfirmDialog open={deleting !== null} title={t("Effacer la demande ?")} confirmLabel={t("Effacer")}
        onCancel={() => setDeleting(null)} onConfirm={remove}>
        {t("Les informations de {name} sont effacées.", { name: `${deleting?.first_name ?? ""} ${deleting?.last_name ?? ""}` })}
        {deleting?.username && ` ${t("Le compte « {name} » reste : le supprimer depuis la page Comptes.", { name: deleting.username })}`}
      </ConfirmDialog>
      <Modal open={link !== null} title={t("Invitation à transmettre")} onClose={() => setLink(null)}
        footer={<Button variant="primary" onClick={() => setLink(null)}>{t("Fermer")}</Button>}>
        <div className="flex flex-col gap-3">
          <p className="text-[13px] text-muted">
            {link?.error ? t("L'e-mail n'est pas parti ({error}).", { error: link.error }) : t("L'e-mail n'est pas parti.")}{" "}
            {t("Transmettez ce lien à {who} par un autre moyen : il sert une seule fois, pendant 48 heures.", { who: link?.who ?? "" })}
          </p>
          {link && <CopyField value={link.url} />}
        </div>
      </Modal>
    </div>
  );
}
